import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Constants, NewwireRegTypes, recipientType } from '../../../shared/utilities/Constants';
import { RadioControl } from '../../models/form/FormControlRadio';
import { FormControlStatusTemp } from '../../models/form/FormControlStatusTemp';
import { selectWireEscrowSelection, selectWireInfo } from '../../reducers';
import { createControl } from '../../utilities/formUtilities';

@Component({
  standalone: false,
  selector: 'mm-wire-recipient-interim',
  templateUrl: './wire-recipient-interim.component.html',
  styleUrls: ['./wire-recipient-interim.component.scss']
})
export class WireRecipientInterimComponent implements OnInit, OnDestroy {
  @Input() parentForm: UntypedFormGroup;
  @Input() useSloa: boolean;
  @Output() recipientSelectionComplete = new EventEmitter<boolean>();
  @Input() isIrsAba: boolean;
  private readonly controlNames = {
    recipient: 'recipient',
    isEscrow: 'isEscrow'
  };

  private readonly recipientControlValues = {
    firstparty: 'firstParty',
    thirdParty: 'thirdParty',
    firstThirdPartyCombined: 'firstParty/thirdParty'
  }

  recipientModel: RadioControl = {
    title: '',
    controlName: this.controlNames.recipient,
    radios: [
      {
        value: this.recipientControlValues.firstparty,
        label: ['First party (Same name as Schwab account holder)'],
      },
      {
        value: this.recipientControlValues.thirdParty,
        label: ['Third party (Any other person or business)'],
      },
    ],
    validationMessage: 'Select recipient'
  }
  showEscrowOrTitleCompanyCheckbox: boolean;
  isEscrow$: Observable<boolean>;
  warningTitle: string = Constants.escrowOrTitleCompanyWarningTitle;
  warningDescription: string = Constants.escrowOrTitleCompanyWarningDescription;
  destroy$ = new Subject<void>();

  constructor(private fb: UntypedFormBuilder, private store: Store) { }

  ngOnInit(): void {
    createControl(this.parentForm, this.controlNames.recipient, '', [Validators.required], undefined, this.useSloa);
    createControl(this.parentForm, this.controlNames.isEscrow, false, undefined, undefined, this.useSloa);
    this.store.select(selectWireInfo).pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(wireInfo => {
      this.isIrsAba = wireInfo.isIrsAba;
      // we want the radio button to be defaulted to third party and disable first party for irs aba
       this.recipientModel.radios.forEach(radio => {
          if (this.isIrsAba && radio.value !== 'thirdParty' ) {
            radio.disabled = true;
            this.recipientControl?.setValue('thirdParty');
          } else {
            radio.disabled = false;
          }
        });
    });
    
    this.recipientControl?.statusChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((status: FormControlStatusTemp) => {
        let value: string = this.recipientControl?.value;
        if (this.useSloa) {
          this.recipientSelectionComplete.emit((status === 'DISABLED') && (value !== recipientType.unselected));
        } else {
          this.recipientSelectionComplete.emit(status === 'VALID');
        }
      });

    this.isEscrow$ = this.store.select(selectWireEscrowSelection);

    this.recipientControl?.valueChanges.subscribe(value => {
      if (value === this.recipientControlValues.firstparty) {
        this.showEscrowOrTitleCompanyCheckbox = false;
        this.isEscrowControl?.setValue(false);
      }
      if (value === this.recipientControlValues.thirdParty || value === this.recipientControlValues.firstThirdPartyCombined) {
        this.showEscrowOrTitleCompanyCheckbox = true;
      }
    });

    // fire events after control has been created so that the statusChanges and valueChanges above fire after edit
    this.recipientControl?.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    Object.keys(this.controlNames).forEach((name) => {
      const names = this.controlNames;
      this.parentForm?.removeControl(this.controlNames[name as keyof typeof names]);
    });

    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  get recipientControl(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.recipient) as UntypedFormControl;
  }

  get isEscrowControl(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.isEscrow) as UntypedFormControl;
  }

  isNewRegistrationType(regShortName: string): boolean {
    return NewwireRegTypes.includes(regShortName.toUpperCase());
  }
}
